import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Button } from '../components/ui/button';
import { MultiSelectPopover } from '../components/ui/multi-select-popover';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import {
  ResponsiveContainer, ComposedChart, BarChart, Bar, LineChart, Line,
  XAxis, YAxis, CartesianGrid, Tooltip as RTooltip, Legend, Cell,
} from 'recharts';
import { TrendingUp, Filter, Loader2, Trophy, TrendingDown, Percent } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import {
  getFrontendAnalytics,
  getFrontendPipelineProducts,
  getUsers,
} from '../lib/api';
import PeriodPresets, { PERIOD_PRESETS } from '../components/PeriodPresets';

const fmtCur = (v) => (v == null || v === '') ? '-' : Number(v).toLocaleString('it-IT', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 });
const fmtNum = (v) => (v == null || v === '') ? '-' : Number(v).toLocaleString('it-IT');

function KPICard({ icon: Icon, label, value, subValue, color = 'bg-slate-50 text-slate-700', testId }) {
  return (
    <Card data-testid={testId}>
      <CardContent className="pt-4">
        <div className="flex items-center gap-2 text-xs text-gray-500">
          <div className={`w-7 h-7 rounded-lg flex items-center justify-center ${color}`}><Icon className="w-3.5 h-3.5" /></div>
          <span>{label}</span>
        </div>
        <div className="mt-1.5 text-xl font-bold text-gray-900">{value}</div>
        {subValue && <div className="text-[11px] text-gray-500 mt-0.5">{subValue}</div>}
      </CardContent>
    </Card>
  );
}

export default function FrontendAnalyticsPage() {
  const { toast } = useToast();
  const defaultPreset = PERIOD_PRESETS.find(p => p.value === 'last_12_months');
  const defaultRange = defaultPreset.getRange();
  const [activePreset, setActivePreset] = useState('last_12_months');
  const [startDate, setStartDate] = useState(defaultRange.start);
  const [endDate, setEndDate] = useState(defaultRange.end);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pmList, setPmList] = useState([]);
  const [amList, setAmList] = useState([]);
  const [productList, setProductList] = useState([]);
  const [buList, setBuList] = useState([]);
  const [selectedPmIds, setSelectedPmIds] = useState([]);
  const [selectedAmIds, setSelectedAmIds] = useState([]);
  const [selectedProductIds, setSelectedProductIds] = useState([]);
  const [selectedBuIds, setSelectedBuIds] = useState([]);

  const handlePresetChange = (presetValue) => {
    setActivePreset(presetValue);
    if (presetValue === 'all') {
      setStartDate('');
      setEndDate('');
    } else if (presetValue === 'custom') {
      // keep current dates, just show inputs
    } else {
      const p = PERIOD_PRESETS.find(x => x.value === presetValue);
      if (p) {
        const r = p.getRange();
        setStartDate(r.start);
        setEndDate(r.end);
      }
    }
  };

  useEffect(() => {
    getUsers('project_manager').then(res => setPmList(res.data || [])).catch(() => {});
    getUsers('account').then(res => setAmList(res.data || [])).catch(() => {});
    getFrontendPipelineProducts().then(res => {
      setProductList(res.data?.products || []);
      setBuList(res.data?.business_units || []);
    }).catch(() => {});
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (startDate) params.start_date = startDate;
      if (endDate) params.end_date = endDate;
      if (selectedPmIds.length) params.pm_id = selectedPmIds.join(',');
      if (selectedAmIds.length) params.account_manager_id = selectedAmIds.join(',');
      if (selectedProductIds.length) params.product_id = selectedProductIds.join(',');
      if (selectedBuIds.length) params.business_unit_id = selectedBuIds.join(',');
      const res = await getFrontendAnalytics(params);
      setData(res.data);
    } catch (err) {
      toast({ title: 'Errore', description: err.response?.data?.detail || err.message, variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate, selectedPmIds, selectedAmIds, selectedProductIds, selectedBuIds, toast]);

  useEffect(() => { load(); }, [load]);

  const kpis = data?.kpis || {};
  const trend = data?.trend || [];
  const byPm = data?.by_pm || [];
  const byAm = data?.by_account_manager || [];
  const byProduct = data?.by_product || [];
  const byBu = data?.by_business_unit || [];

  return (
    <div className="space-y-4">
      {/* Filters */}
      <Card>
        <CardContent className="pt-4 space-y-3">
          <div className="flex items-center gap-2 text-sm text-gray-500"><Filter className="w-4 h-4" /> Filtri periodo</div>
          <PeriodPresets
            activePreset={activePreset}
            onPresetChange={handlePresetChange}
            showCustomDates={activePreset === 'custom'}
            startDate={startDate}
            endDate={endDate}
            onStartDateChange={setStartDate}
            onEndDateChange={setEndDate}
            testIdPrefix="fe-analytics"
          />
          <div className="flex flex-wrap items-center gap-2 pt-2 border-t">
            <span className="text-xs text-gray-500 mr-1">Filtri aggiuntivi:</span>
            <MultiSelectPopover
              items={pmList.map(u => ({ id: u.id, label: u.name || u.email }))}
              selectedIds={selectedPmIds}
              onChange={setSelectedPmIds}
              placeholder="Tutti i PM" width="w-[170px]" testId="fe-analytics-pm-filter"
            />
            <MultiSelectPopover
              items={amList.map(u => ({ id: u.id, label: u.name || u.email }))}
              selectedIds={selectedAmIds}
              onChange={setSelectedAmIds}
              placeholder="Tutti gli Account" width="w-[180px]" testId="fe-analytics-am-filter"
            />
            <MultiSelectPopover
              items={buList.map(b => ({ id: b.id, label: b.name }))}
              selectedIds={selectedBuIds}
              onChange={setSelectedBuIds}
              placeholder="Tutte le BU" width="w-[160px]" testId="fe-analytics-bu-filter"
            />
            <MultiSelectPopover
              items={productList.map(p => ({ id: p.id, label: p.name }))}
              selectedIds={selectedProductIds}
              onChange={setSelectedProductIds}
              placeholder="Tutti i prodotti" width="w-[180px]" testId="fe-analytics-product-filter"
            />
            {(selectedPmIds.length || selectedAmIds.length || selectedProductIds.length || selectedBuIds.length) > 0 && (
              <Button variant="ghost" size="sm" onClick={() => { setSelectedPmIds([]); setSelectedAmIds([]); setSelectedProductIds([]); setSelectedBuIds([]); }}>Reset</Button>
            )}
          </div>
        </CardContent>
      </Card>

      {loading ? (
        <div className="flex items-center justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-gray-400" /></div>
      ) : (
        <>
          {/* KPIs */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
            <KPICard icon={TrendingUp} label="Nuove in pipeline" value={fmtNum(kpis.entered_count)} subValue="basato su data firma contratto" color="bg-blue-50 text-blue-700" testId="kpi-entered" />
            <KPICard icon={Trophy} label="Esitati" value={fmtNum(kpis.closed_count)} color="bg-gray-100 text-gray-700" testId="kpi-closed" />
            <KPICard icon={Trophy} label="Vinti" value={fmtNum(kpis.won_count)} color="bg-emerald-50 text-emerald-700" testId="kpi-won" />
            <KPICard icon={TrendingDown} label="Persi" value={fmtNum(kpis.lost_count)} color="bg-red-50 text-red-700" testId="kpi-lost" />
            <KPICard icon={Percent} label="Conversione" value={`${kpis.conversion_rate_pct || 0}%`} color="bg-violet-50 text-violet-700" testId="kpi-conv" />
            <KPICard icon={TrendingUp} label="Venduto post-FE" value={fmtCur(kpis.won_generated_value)} subValue={kpis.uplift_pct != null ? `${kpis.uplift_pct >= 0 ? '+' : ''}${kpis.uplift_pct}% vs stima` : ''} color="bg-amber-50 text-amber-700" testId="kpi-value" />
          </div>

          {/* Charts riga 1 */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium">Front-End Esitati nel Tempo</CardTitle>
                <p className="text-xs text-gray-500">Esitati / Vinti / Persi (linee) + Volume € generato (barra)</p>
              </CardHeader>
              <CardContent className="pt-0">
                <ResponsiveContainer width="100%" height={280}>
                  <ComposedChart data={trend} margin={{ top: 5, right: 10, left: -10, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis dataKey="period" tick={{ fontSize: 11 }} />
                    <YAxis yAxisId="left" tick={{ fontSize: 11 }} />
                    <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 11 }} tickFormatter={(v) => v >= 1000 ? `€${(v/1000).toFixed(0)}k` : `€${v}`} />
                    <RTooltip formatter={(v, n) => n === 'Volume €' ? fmtCur(v) : v} />
                    <Legend wrapperStyle={{ fontSize: '11px' }} />
                    <Bar yAxisId="right" dataKey="won_value" name="Volume €" fill="#c084fc" opacity={0.55} radius={[4, 4, 0, 0]} />
                    <Line yAxisId="left" type="monotone" dataKey="outcome" name="Esitati" stroke="#6b7280" strokeWidth={2} dot={{ r: 3 }} />
                    <Line yAxisId="left" type="monotone" dataKey="won" name="Vinti" stroke="#10b981" strokeWidth={2} dot={{ r: 3 }} />
                    <Line yAxisId="left" type="monotone" dataKey="lost" name="Persi" stroke="#ef4444" strokeWidth={2} dot={{ r: 3 }} />
                  </ComposedChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium">Tasso di Conversione Front-End</CardTitle>
                <p className="text-xs text-gray-500">% Vinti / Esitati per mese</p>
              </CardHeader>
              <CardContent className="pt-0">
                <ResponsiveContainer width="100%" height={280}>
                  <BarChart data={trend} margin={{ top: 5, right: 10, left: -10, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis dataKey="period" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => `${v}%`} domain={[0, 100]} />
                    <RTooltip formatter={(v) => `${v}%`} />
                    <Bar dataKey="conversion_rate_pct" name="Conversione" fill="#8b5cf6" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </div>

          {/* Breakdown tables */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <BreakdownCard title="Breakdown per PM" data={byPm} labelKey="name" testId="breakdown-pm" />
            <BreakdownCard title="Breakdown per Account Manager" data={byAm} labelKey="name" testId="breakdown-am" />
            <BreakdownCard title="Breakdown per Prodotto" data={byProduct} labelKey="name" testId="breakdown-product" />
            <BreakdownCard title="Breakdown per Business Unit" data={byBu} labelKey="name" testId="breakdown-bu" />
          </div>
        </>
      )}
    </div>
  );
}

function BreakdownCard({ title, data, labelKey, testId }) {
  return (
    <Card data-testid={testId}>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-medium">{title}</CardTitle>
      </CardHeader>
      <CardContent className="pt-0">
        {data.length === 0 ? (
          <p className="text-xs text-gray-400 text-center py-6">Nessun dato</p>
        ) : (
          <div className="max-h-72 overflow-y-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="text-[11px]">Nome</TableHead>
                  <TableHead className="text-[11px] text-right">Esit.</TableHead>
                  <TableHead className="text-[11px] text-right">V</TableHead>
                  <TableHead className="text-[11px] text-right">Conv%</TableHead>
                  <TableHead className="text-[11px] text-right">Volume</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.slice(0, 30).map((r, i) => (
                  <TableRow key={i}>
                    <TableCell className="text-xs font-medium truncate max-w-[180px] whitespace-nowrap" title={r[labelKey]}>{r[labelKey]}</TableCell>
                    <TableCell className="text-xs text-right">{fmtNum(r.outcome)}</TableCell>
                    <TableCell className="text-xs text-right text-emerald-700 font-medium">{fmtNum(r.won)}</TableCell>
                    <TableCell className="text-xs text-right text-violet-700 font-medium">{r.conversion_rate_pct}%</TableCell>
                    <TableCell className="text-xs text-right font-semibold">{fmtCur(r.won_value)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
